import { styled, Box } from '@mui/material';

/**
 * MinimalCard - A clean, minimal card component with theme-aware dark styling
 *
 * Matches the visual design of tables and form cards with:
 * - Dark mode background: rgba(15, 23, 42, 0.85)
 * - 16px border radius
 * - Backdrop blur effect
 * - Subtle borders and shadows
 * - Smooth hover transitions
 *
 * Used across TeamsV2Page and TeamsOverviewPage for consistent card UI.
 *
 * @param $hoverEffect - Optional: Adds hover effects including transform and enhanced shadow (transient prop, doesn't pass to DOM)
 *
 * @see src/modules/TeamModule/components/TeamsV2Page.tsx
 * @see src/modules/ReportsModule/TeamsOverviewPage.tsx
 * @see src/styles/index.ts StyledSimpleCard for similar styling pattern
 */
export const MinimalCard = styled(Box)<{ $hoverEffect?: boolean }>(
  ({ theme, $hoverEffect = true }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(24px)',
    border:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(148, 163, 184, 0.12)'
        : '1px solid rgba(226, 232, 240, 0.8)',
    borderRadius: '16px',
    padding: theme.spacing(3),
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset, 0 0 12px rgba(59, 130, 246, 0.06)'
        : '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04), 0 0 12px rgba(59, 130, 246, 0.04)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '& h3': {
      color: theme.palette.mode === 'dark' ? 'rgba(226, 232, 240, 0.95)' : 'rgba(15, 23, 42, 0.95)',
      fontWeight: 600,
      fontSize: '1.125rem',
      letterSpacing: '-0.01em',
    },
    ...($hoverEffect && {
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08) inset, 0 0 16px rgba(59, 130, 246, 0.08)'
            : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04), 0 0 16px rgba(59, 130, 246, 0.06)',
      },
    }),
  }),
);
